import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '__components/Kizen/IconButton';
import { StyledVariableCard, StyledVariableWarpper } from './styles';
import Icon from '__components/Kizen/Icon';
import { IconSizing } from '__components/Kizen/Icon';
import { NameInput } from './NameInput';
import { BusinessVariableErrors, useErrors } from './useErrors';
import { ValueInput } from './ValueInput';
import { BusinessVariable } from 'components/WYSIWYG/use-merge-fields';

const buttonColor = {
  default: 'var(--color-background-dark)',
  hover: 'var(--color-background-darker)',
};

export type OnChangeBusinessVariableHandler = ({
  id,
  prop,
  value,
}: {
  id: string;
  prop: keyof BusinessVariable;
  value: BusinessVariable[keyof BusinessVariable];
}) => BusinessVariable[keyof BusinessVariable];

type BusinessVariableSettingsProps = {
  variable: BusinessVariable;
  index: number;
  onChange: OnChangeBusinessVariableHandler;
  onDelete: (id: string, index: number) => void;
  indexToScroll: null | number;
  setIndexToScroll: Dispatch<SetStateAction<null | number>>;
  errors: BusinessVariableErrors;
  setErrors: Dispatch<SetStateAction<BusinessVariableErrors>>;
};

export const BusinessVariableSettings = ({
  variable,
  index,
  onChange,
  onDelete,
  indexToScroll,
  setIndexToScroll,
  errors,
  setErrors,
}: BusinessVariableSettingsProps) => {
  const { t } = useTranslation();

  const { id, name, value_string } = variable;

  const { hasError, variableError, errorKey, validate, clearError } = useErrors(
    { errors, setErrors, index }
  );

  const onDeleteVariable = () => {
    onDelete(id, index);
  };

  const onChangeName = (value: string) => {
    const result = onChange({ id, prop: 'name', value });
    if (result) {
      clearError('name');
    }
    return result as string;
  };
  const onChangeValue = (value: string) => {
    const result = onChange({ id, prop: 'value_string', value });
    if (result) {
      clearError('value_string');
    }
    return result as string;
  };

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errorKey) {
      wrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [errorKey]);

  useEffect(() => {
    if (indexToScroll === index) {
      setIndexToScroll(null);
      wrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [indexToScroll, index, setIndexToScroll]);

  return (
    <StyledVariableWarpper ref={wrapperRef} data-qa={id} data-qa-index={index}>
      <IconButton
        title={t('Delete')}
        sizing="dense"
        color={buttonColor}
        onClick={onDeleteVariable}
        data-qa="delete-business-variable-button"
      >
        <IconSizing size="15px">
          <Icon icon="trash" />
        </IconSizing>
      </IconButton>
      <StyledVariableCard hasError={hasError}>
        <NameInput
          value={name}
          onChange={onChangeName}
          error={variableError?.name}
          validate={errorKey === 'name' ? validate : undefined}
        />
        <ValueInput
          value={value_string}
          onChange={onChangeValue}
          error={variableError?.value_string}
          validate={errorKey === 'value_string' ? validate : undefined}
        />
      </StyledVariableCard>
    </StyledVariableWarpper>
  );
};
